import type { RolePerson, RuleTextPart } from '@k-management/shared';
import { ruleTextParts } from '@k-management/shared';
import { Fragment, type ReactNode } from 'react';

/** A person of a role with the label «INT» or «EST». */
export function Person({ person, highlight }: { person: RolePerson; highlight?: string }) {
  return (
    <span className="org-person">
      <Highlighted text={person.name} query={highlight} />
      <span className={`org-kind ${person.kind === 'EST' ? 'est' : 'int'}`}>{person.kind}</span>
    </span>
  );
}

export function People({ people, highlight }: { people: RolePerson[]; highlight?: string }) {
  if (people.length === 0) return <span className="muted">—</span>;
  return (
    <span className="org-people">
      {people.map((person, index) => (
        <Person key={`${person.name}-${index}`} person={person} highlight={highlight} />
      ))}
    </span>
  );
}

/** The text with the searched words marked. */
export function Highlighted({ text, query }: { text: string; query?: string }): ReactNode {
  const needle = query?.trim().toLowerCase();
  if (!needle) return text;
  const parts: ReactNode[] = [];
  const lower = text.toLowerCase();
  let from = 0;
  for (let at = lower.indexOf(needle); at >= 0; at = lower.indexOf(needle, from)) {
    if (at > from) parts.push(text.slice(from, at));
    parts.push(<mark key={at}>{text.slice(at, at + needle.length)}</mark>);
    from = at + needle.length;
  }
  if (from < text.length) parts.push(text.slice(from));
  return <>{parts}</>;
}

/** The text of a rule: bold where marked, thresholds as labels. */
export function RuleText({ text, query }: { text: string; query?: string }) {
  return (
    <>
      {ruleTextParts(text).map((part: RuleTextPart, index) => {
        const content = <Highlighted text={part.text} query={query} />;
        if (part.kind === 'bold') return <strong key={index}>{content}</strong>;
        if (part.kind === 'value')
          return (
            <span key={index} className="org-value">
              {content}
            </span>
          );
        return <Fragment key={index}>{content}</Fragment>;
      })}
    </>
  );
}
